import { useMemo, useState } from "react";
import { Check, Circle, Eye, EyeOff, FileText, Lock, Pencil } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/design-system/components/ui";
import { useStageTransitions, useDealIntakeBundle, useDealSubmissionAnswers, useIntakeChecklist, useDocumentRequests } from "@/hooks/deals/useDealDetail";
import { useAuth } from "@/hooks/useAuth";
import { dealLabel, formatCurrency } from "@/components/deals/dealFormat";
import { cn } from "@/design-system/lib/utils";
import { parseFormSchema, type FormField, type VisibleWhen } from "@/lib/formSchema";
import { normalizeDealSubmissionFinancialSchema } from "@/lib/dealSubmissionFinancialLabels";
import {
  ACTIVE_STAGES,
  DEAL_PERMISSIONS,
  DEAL_TYPE_LABEL,
  STAGE_LABEL,
  SUB_STATE_LABEL,
  subStateLabelFor,
  type DealDetailDto,
  type DealListItemDto,
  type DealPipelineStage,
  type RealEstateIncluded,
} from "@/types/deals";
import { CounterpartyCard } from "./CounterpartyCard";
import { DealFlowOverviewPanels } from "@/components/DealFlow/OverviewPanels/DealFlowOverviewPanels";
import { useDateFormatter } from "@/utils/datetime";

interface Props {
  deal: DealDetailDto;
}

const DEAL_FLOW_PANEL_STAGES: DealPipelineStage[] = ["Underwriting", "Closing", "Closed"];
// A Declined or Withdrawn deal shows the panels only if it reached Underwriting,
// which the server reads from its stage history (owner ruling, 2026-10-26).
const STOPPED_STAGES: DealPipelineStage[] = ["Declined", "Withdrawn"];

const REAL_ESTATE_LABEL: Record<RealEstateIncluded, string> = {
  None: "Not included",
  Owned: "Included (owned)",
  Leased: "Included (leased)",
};

export function DealOverviewTab({ deal }: Props) {
  const transitions = useStageTransitions(deal.id);
  const showDealFlowPanels = DEAL_FLOW_PANEL_STAGES.includes(deal.stage);
  const stopped = STOPPED_STAGES.includes(deal.stage);

  return (
    <div className="flex flex-col gap-space-4">
      <DealInfoCard deal={deal} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-space-3">
        <CounterpartyCard role="Broker" kind="Broker" brokerId={deal.brokerId} />
        <BorrowerCard deal={deal} />
        <SellerCard deal={deal} />
        {deal.businessId ? (
          <CounterpartyCard role="Business" kind="Business" businessId={deal.businessId} />
        ) : (
          <CounterpartyCard
            role="Business"
            kind="Empty"
            emptyHint="No Business attached — add one in the Intake tab."
          />
        )}
      </div>

      {showDealFlowPanels && <DealFlowOverviewPanels dealId={deal.id} />}
      {stopped && <DealFlowOverviewPanels dealId={deal.id} stoppedDeal />}

      <StageProgressionTimeline
        currentStage={deal.stage}
        transitions={transitions.data ?? []}
      />

      <SubStateExplainerCard deal={deal} />
    </div>
  );
}

function BorrowerCard({ deal }: { deal: DealDetailDto }) {
  if (deal.borrowerPartyId) {
    return (
      <CounterpartyCard
        role="Borrower"
        kind="Party"
        partyType="Borrower"
        partyId={deal.borrowerPartyId}
      />
    );
  }
  return (
    <CounterpartyCard
      role="Borrower"
      kind="Empty"
      emptyHint={
        deal.stage === "Intake"
          ? "No Borrower attached — add one in the Intake tab."
          : "No Borrower attached."
      }
    />
  );
}

function SellerCard({ deal }: { deal: DealDetailDto }) {
  if (deal.sellerPartyId) {
    return (
      <CounterpartyCard
        role="Seller"
        kind="Party"
        partyType="Seller"
        partyId={deal.sellerPartyId}
      />
    );
  }
  return (
    <CounterpartyCard
      role="Seller"
      kind="Empty"
      emptyHint={
        deal.stage === "Intake"
          ? "No Seller attached — add one in the Intake tab."
          : "No Seller attached."
      }
    />
  );
}

function DealInfoCard({ deal }: { deal: DealDetailDto }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // DealTypeLockedAt is an instant, rendered in the viewer's zone.
  const { date } = useDateFormatter();
  const dealTypeLockLine = deal.dealTypeLockedAt
    ? `Locked on ${date(deal.dealTypeLockedAt)}, when the deal entered Pre-Approval.`
    : null;
  return (
    <Card>
      <div className="flex items-start justify-between gap-space-3 mb-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            {dealLabel(deal as unknown as DealListItemDto)}
          </div>
          <p className="text-small text-gray-mid m-0 mt-space-1">
            Clean deal summary. Use the eye control for full broker-submitted details.
          </p>
        </div>
        <div className="flex items-center gap-space-2 shrink-0">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-light hover:bg-white-warm"
            aria-label={expanded ? "Hide deal details" : "Show deal details"}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
          </button>
          {hasPermission(DEAL_PERMISSIONS.Update) && !deal.isArchived && (
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-light hover:bg-white-warm"
              aria-label="Edit deal details"
              onClick={() => navigate(`/deal-flow/deals/${deal.id}/edit`)}
            >
              <Pencil size={16} aria-hidden />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-space-3 text-small">
        <Field label="Deal #">{deal.dealNumber}</Field>
        <Field label="Business / deal name">{dealLabel(deal as unknown as DealListItemDto)}</Field>
        <Field label="Deal type">
          <span className="inline-flex items-center gap-space-1">
            {DEAL_TYPE_LABEL[deal.dealType ?? "Acquisition"]}
            {dealTypeLockLine && (
              <span role="img" aria-label={dealTypeLockLine} title={dealTypeLockLine} className="inline-flex text-gray-mid">
                <Lock size={12} aria-hidden />
              </span>
            )}
          </span>
        </Field>
        <Field label="Purchase price">{formatCurrency(deal.purchasePrice)}</Field>
        <Field label="Seller note">{formatCurrency(deal.sellerNoteAmount)}</Field>
        <Field label="Cash at closing">
          {deal.cashAtClosing != null ? formatCurrency(deal.cashAtClosing) : "—"}
        </Field>
        <Field label="Transaction status">{deal.transactionStatus ?? "—"}</Field>
        <Field label="Target close">{deal.targetCloseDate ?? "—"}</Field>
        <Field label="Broker liaison">
          {deal.assignedC1Id ? deal.assignedC1DisplayName?.trim() || "Needs assignment" : "Unassigned"}
        </Field>
      </div>

      {expanded && <DealSubmissionDetailsPanel deal={deal} />}
      <SubmissionSummaryLink dealId={deal.id} />
    </Card>
  );
}

export function DealSubmissionDetailsPanel({ deal }: { deal: DealDetailDto }) {
  const answers = useDealSubmissionAnswers(deal.id);
  if (answers.isLoading) {
    return <p className="text-small text-gray-mid mt-space-4 m-0">Loading submission details…</p>;
  }
  if (!answers.data) {
    return <NormalizedDealDetails deal={deal} />;
  }

  return (
    <div className="mt-space-4 pt-space-4 border-t border-border-light">
      <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-3">
        Submission Details
      </div>
      <div className="flex flex-col gap-space-4">
        {answers.data.sections.map((section) => {
          const parsed = parseFormSchema(section.formSchemaJson);
          const schema = parsed ? normalizeDealSubmissionFinancialSchema(parsed) : null;
          const data = parseData(section.savedDataJson);
          if (!schema) return null;
          const renderable = schema.fields.filter((field) => shouldRenderField(field, data));
          if (renderable.length === 0) return null;
          return (
            <section key={section.sectionId} className="rounded-md border border-border-light p-space-3 bg-white-pure">
              <h3 className="font-display text-h4 text-onyx m-0 mb-space-3">{section.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 text-small">
                {renderable.map((field) => (
                  <SubmissionField key={field.name} field={field} value={data[field.name]} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function NormalizedDealDetails({ deal }: { deal: DealDetailDto }) {
  const { date } = useDateFormatter();
  return (
    <div className="mt-space-4 pt-space-4 border-t border-border-light">
      <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-3">
        Deal details
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-space-3 text-small">
        <Field label="Subordinated debt">
          {deal.subordinatedDebtAmount != null ? formatCurrency(deal.subordinatedDebtAmount) : "—"}
        </Field>
        <Field label="Real estate">
          {deal.realEstateIncluded ? REAL_ESTATE_LABEL[deal.realEstateIncluded] : "—"}
        </Field>
        <Field label="Collateral type">{deal.collateralType ?? "—"}</Field>
        <Field label="NAICS">{deal.naicsInitial ?? "—"}</Field>
        <Field label="Created">{date(deal.createdAt)}</Field>
      </div>
      {deal.dealNarrative && (
        <div className="mt-space-3 pt-space-3 border-t border-border-light">
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-1">
            Deal narrative
          </div>
          <p className="text-small text-onyx whitespace-pre-wrap m-0">{deal.dealNarrative}</p>
        </div>
      )}
    </div>
  );
}

function SubmissionField({ field, value }: { field: FormField; value: unknown }) {
  if (field.type === "repeatgroup") {
    const rows = Array.isArray(value) ? value.filter(isRecord) : [];
    const columns = (field.fields ?? []).filter((nested) => !isNonAnswerField(nested));
    return (
      <div className="md:col-span-2">
        <div className="font-display text-caption uppercase tracking-label text-gray-mid mb-space-1">
          {field.label}
        </div>
        {rows.length === 0 || columns.length === 0 ? (
          <div className="text-onyx">—</div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border-light">
            <table className="min-w-full text-small">
              <thead className="bg-white-warm">
                <tr>
                  {columns.map((column) => (
                    <th key={column.name} className="text-left font-display text-caption uppercase tracking-label text-gray-mid px-space-2 py-space-2">
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="border-t border-border-light">
                    {columns.map((column) => (
                      <td key={column.name} className="px-space-2 py-space-2 text-onyx">
                        {formatAnswer(row[column.name], column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <Field label={field.label} wide={field.type === "textarea"}>
      {formatAnswer(value, field)}
    </Field>
  );
}

function shouldRenderField(field: FormField, data: Record<string, unknown>): boolean {
  if (isNonAnswerField(field)) return false;
  return isVisible(field.visibleWhen, data);
}

function isNonAnswerField(field: FormField): boolean {
  return ["sectionheading", "instruction", "pagebreak", "fileattach"].includes(field.type);
}

function isVisible(rule: VisibleWhen | undefined, data: Record<string, unknown>): boolean {
  if (!rule) return true;
  const value = data[rule.field];
  if ("equals" in rule && rule.equals !== undefined) return value === rule.equals;
  if (rule.in && rule.in.length > 0) return rule.in.includes(value as string | number | boolean);
  return true;
}

function formatAnswer(value: unknown, field: FormField): string {
  if (value === null || value === undefined || value === "") return "—";
  if (field.type === "checkbox" || field.type === "yesno") return value === true ? "Yes" : value === false ? "No" : String(value);
  if (Array.isArray(value)) return value.length ? value.map((item) => optionLabel(field, item)).join(", ") : "—";
  if (field.type === "currency" && typeof value === "number") return formatCurrency(value);
  if (field.type === "percentage" && typeof value === "number") return `${value}%`;
  return optionLabel(field, value);
}

function optionLabel(field: FormField, value: unknown): string {
  const stringValue = String(value);
  return field.options?.find((option) => option.value === stringValue)?.label ?? stringValue;
}

function parseData(json: string | null): Record<string, unknown> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json) as unknown;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function SubmissionSummaryLink({ dealId }: { dealId: string }) {
  const bundle = useDealIntakeBundle(dealId);
  const [, setParams] = useSearchParams();
  if (!bundle.data?.profile) return null;
  return (
    <div className="mt-space-3 pt-space-3 border-t border-border-light">
      <button
        type="button"
        className="inline-flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
        onClick={() =>
          setParams(
            { tab: "documents", docFilter: "Submission Summary" },
            { replace: false },
          )
        }
      >
        <FileText size={14} aria-hidden />
        View broker submission summary
      </button>
    </div>
  );
}

function Field({ label, children, wide = false }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? "md:col-span-2" : undefined}>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-onyx mt-[2px] whitespace-pre-wrap">{children}</div>
    </div>
  );
}

interface TransitionLike {
  toStage: DealPipelineStage;
  transitionDate: string;
}

function StageProgressionTimeline({
  currentStage,
  transitions,
}: {
  currentStage: DealPipelineStage;
  transitions: TransitionLike[];
}) {
  const { date } = useDateFormatter();
  const enteredByStage = useMemo(() => {
    const map = new Map<DealPipelineStage, string>();
    for (const t of transitions) {
      // Last transition into a stage wins.
      map.set(t.toStage, t.transitionDate);
    }
    return map;
  }, [transitions]);

  const currentIndex = ACTIVE_STAGES.indexOf(currentStage);

  return (
    <Card>
      <div className="font-display font-bold text-h3 text-onyx mb-space-3">
        Stage progression
      </div>
      <ol className="flex items-center gap-space-2 m-0 p-0 list-none overflow-x-auto">
        {ACTIVE_STAGES.map((stage, i) => {
          const reached = currentIndex >= 0 && i <= currentIndex;
          const isCurrent = stage === currentStage;
          const enteredAt = enteredByStage.get(stage);
          return (
            <li key={stage} className="flex items-center gap-space-2 shrink-0">
              <div
                className={cn(
                  "h-7 w-7 rounded-pill inline-flex items-center justify-center border",
                  isCurrent
                    ? "bg-gold border-gold text-onyx"
                    : reached
                      ? "bg-status-success/10 border-status-success text-status-success"
                      : "bg-white-warm border-border-light text-gray-mid",
                )}
                title={enteredAt ? `Entered ${date(enteredAt)}` : "Not yet entered"}
              >
                {reached ? <Check size={14} aria-hidden /> : <Circle size={10} aria-hidden />}
              </div>
              <div>
                <div
                  className={cn(
                    "font-display text-label uppercase tracking-label",
                    isCurrent ? "text-onyx" : reached ? "text-status-success" : "text-gray-mid",
                  )}
                >
                  {STAGE_LABEL[stage]}
                </div>
                {enteredAt && (
                  <div className="text-caption text-gray-mid">
                    {date(enteredAt)}
                  </div>
                )}
              </div>
              {i < ACTIVE_STAGES.length - 1 && (
                <div
                  className={cn(
                    "h-[1px] w-12",
                    reached && currentIndex > i ? "bg-status-success" : "bg-border-light",
                  )}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

/** "Underwriting → Assigned to A1", or the stage alone where it has no
 *  sub-state (Closing and Closed). */
function stageWithSubState(deal: DealDetailDto): string {
  const subState = subStateLabelFor(deal.stage, deal.subState);
  return subState ? `${STAGE_LABEL[deal.stage]} → ${subState}` : STAGE_LABEL[deal.stage];
}

function SubStateExplainerCard({ deal }: { deal: DealDetailDto }) {
  const checklist = useIntakeChecklist(deal.stage === "Intake" ? deal.id : undefined);
  const docRequests = useDocumentRequests(deal.stage === "PreApproval" ? deal.id : undefined);

  if (deal.stage === "Intake" && checklist.data) {
    const items = checklist.data.items;
    const required = items.filter((i) => i.required);
    const completed = required.filter((i) => i.status === "Complete" || i.status === "NotApplicable");
    const remaining = required.filter((i) => i.status !== "Complete" && i.status !== "NotApplicable");
    const pct = required.length === 0 ? 100 : Math.round((completed.length / required.length) * 100);

    return (
      <Card featured>
        <div className="font-display font-bold text-h3 text-onyx mb-space-2">
          What&apos;s needed to advance
        </div>
        <p className="text-small text-gray-mid m-0">
          Currently in <strong>{STAGE_LABEL[deal.stage]} → {SUB_STATE_LABEL[deal.subState]}</strong>.
        </p>
        <div className="mt-space-3 grid grid-cols-1 md:grid-cols-2 gap-space-3">
          <Stat label="Required items complete" value={`${completed.length} / ${required.length}`} />
          <Stat label="Completion" value={`${pct}%`} />
        </div>
        {remaining.length > 0 && (
          <ul className="mt-space-3 m-0 p-0 list-none flex flex-col gap-space-1">
            {remaining.slice(0, 5).map((item) => (
              <li key={item.id} className="text-small text-onyx">
                • {item.title}
              </li>
            ))}
            {remaining.length > 5 && (
              <li className="text-caption text-gray-mid italic">
                + {remaining.length - 5} more
              </li>
            )}
          </ul>
        )}
      </Card>
    );
  }

  if (deal.stage === "PreApproval" && docRequests.data) {
    const reqs = docRequests.data;
    const cleared = reqs.filter((r) => r.status === "Approved" || r.status === "Waived");
    const pct = reqs.length === 0 ? 0 : Math.round((cleared.length / reqs.length) * 100);
    return (
      <Card featured>
        <div className="font-display font-bold text-h3 text-onyx mb-space-2">
          What&apos;s needed to advance
        </div>
        <p className="text-small text-gray-mid m-0">
          Currently in <strong>{STAGE_LABEL[deal.stage]} → {SUB_STATE_LABEL[deal.subState]}</strong>.
        </p>
        <div className="mt-space-3 grid grid-cols-1 md:grid-cols-2 gap-space-3">
          <Stat label="Documents cleared" value={`${cleared.length} / ${reqs.length}`} />
          <Stat label="Completion" value={`${pct}%`} />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="font-display font-bold text-h3 text-onyx mb-space-1">
        Stage status
      </div>
      <p className="text-small text-gray-mid m-0">
        Currently in <strong>{stageWithSubState(deal)}</strong>.
      </p>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">{label}</div>
      <div className="font-display text-h3 text-onyx mt-[2px]">{value}</div>
    </div>
  );
}
